import {
  type VerificationRejectionReason,
  type VerificationDocumentType,
  VERIFICATION_REJECTION_REASON_LABELS as REJECTION_REASON_LABELS,
  ACTIVE_REJECTION_REASONS,
} from '@safetyhub/shared';
import api from './api';

export type { VerificationRejectionReason };
export { REJECTION_REASON_LABELS, ACTIVE_REJECTION_REASONS };

export type BusinessStatus = 'ACTIVE' | 'SUSPENDED' | 'CLOSED';

export interface PendingOrg {
  id: string;
  name: string;
  category: string;
  verificationStatus: string;
  registrationNumber: string | null;
  createdAt: string;
  hasBusinessReg: boolean;
  hasDesignation: boolean;
  autoCheckSummary: {
    checksumPassed: boolean;
    ntsPassed: boolean;
    hasMatch: boolean;
  };
  isManualReview: boolean;
  ntsValidation?: {
    businessStatus?: BusinessStatus;
  };
  verifiedAt: string | null;
  verifiedBySuperAdminId: string | null;
  _count: { members: number };
}

export interface VerificationDocument {
  id: string;
  /**
   * ⚠️ **`<img src>`·`<a href>`에 넣지 말 것 — 401이다.** 이 값은 Bearer 전용 라우트이고 열람은
   * {@link downloadVerificationDocument}(blob)로 한다. 형상 호환으로 남아 있다(응답 계약이 조직
   * 상세와 같은 타입을 쓴다 — 서버 `organizations/agency-verification-document.ts`).
   */
  url: string;
  fileName: string;
  uploadedAt: string;
  /**
   * 서류 종류 — **화면이 쓰는 값이므로 선언에 있어야 한다**(2026-10-19). 종전엔 서버가 보내는데
   * 여기 선언이 없어 여분 키로 흘렀고, 그래서 「지정서 목록 (N건)」 제목이 **사업자등록증까지
   * 세면서** 그것을 구별해 보여 줄 수단이 없었다. 라벨 소유자는 shared
   * `VERIFICATION_DOCUMENT_TYPE_LABELS`다 — 여기서 한국어를 새로 적지 말 것.
   *
   * optional인 이유는 **옛 번들과의 호환**이다(계약이 그렇다 — 서버가 채우는 값은 항상 있다).
   */
  type?: VerificationDocumentType;
}

export interface FuzzyMatch {
  name: string;
  similarity: number;
  id: string;
}

export interface AutoCheckResult {
  verificationStatus?: 'VERIFIED' | 'PENDING' | 'REVIEW_NO_MATCH' | 'REVIEW_AMBIGUOUS' | 'INFO_MISMATCH';
  checksumValid?: boolean;
  ntsValidation?: {
    valid: boolean;
    status?: unknown;
    reason?: string;
    businessStatus?: BusinessStatus;
  } | null;
  fuzzyMatch?: FuzzyMatch[];
  summary?: string;
  [key: string]: unknown;
}

export interface VerificationDetail {
  id: string;
  name: string;
  category: string;
  verificationStatus: string;
  verifiedAt: string | null;
  rejectionReason: string | null;
  rejectionReasonCode: VerificationRejectionReason | null;
  registrationNumber: string | null;
  openingDate: string | null;
  ceoName: string | null;
  officialName: string | null;
  address: string | null;
  agencyAutoCheckResult: AutoCheckResult | null;
  verificationDocuments: VerificationDocument[] | null;
  isManualReview: boolean;
  ntsValidation?: {
    businessStatus?: BusinessStatus;
  };
  createdAt: string;
  ownerUser: {
    id: string;
    name: string;
    phone: string | null;
  } | null;
}

export interface UpdateVerificationFieldsPayload {
  name?: string;
  category?: string;
  registrationNumber?: string;
  openingDate?: string;
  ceoName?: string;
  officialName?: string;
  address?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error: null | { code: string; message: string };
  meta?: { page: number; limit: number; total: number };
}

export type VerificationStatusFilter = 'pending' | 'rejected' | 'verified' | 'all';

export async function getPendingVerifications(params?: {
  page?: number;
  limit?: number;
  category?: string;
  manualReviewOnly?: boolean;
  statusFilter?: VerificationStatusFilter;
}): Promise<ApiResponse<PendingOrg[]> & { meta: { page: number; limit: number; total: number } }> {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.category) query.set('category', params.category);
  if (params?.manualReviewOnly) query.set('manualReviewOnly', 'true');
  if (params?.statusFilter) query.set('statusFilter', params.statusFilter);

  const qs = query.toString();
  const res = await api.get<ApiResponse<PendingOrg[]>>(
    `/admin/verification/pending${qs ? `?${qs}` : ''}`,
  );
  return res.data as ApiResponse<PendingOrg[]> & { meta: { page: number; limit: number; total: number } };
}

export async function getVerificationDetail(
  companyId: string,
): Promise<ApiResponse<VerificationDetail>> {
  const res = await api.get<ApiResponse<VerificationDetail>>(
    `/admin/verification/companies/${companyId}`,
  );
  return res.data;
}

export async function approveVerification(
  companyId: string,
  reason?: string,
): Promise<ApiResponse<{ message: string }>> {
  const res = await api.post<ApiResponse<{ message: string }>>(
    `/admin/verification/companies/${companyId}/approve`,
    { reason },
  );
  return res.data;
}

export async function rejectVerification(
  companyId: string,
  reasonCode: VerificationRejectionReason,
  memo?: string,
): Promise<ApiResponse<{ message: string }>> {
  const res = await api.post<ApiResponse<{ message: string }>>(
    `/admin/verification/companies/${companyId}/reject`,
    { reasonCode, memo },
  );
  return res.data;
}

export async function updateVerificationFields(
  companyId: string,
  payload: UpdateVerificationFieldsPayload,
): Promise<ApiResponse<{ changed: boolean; verificationStatus: string; revalidated?: boolean }>> {
  const res = await api.patch<ApiResponse<{ changed: boolean; verificationStatus: string; revalidated?: boolean }>>(
    `/admin/verification/companies/${companyId}/verification-fields`,
    payload,
  );
  return res.data;
}

export async function revalidateVerification(
  companyId: string,
): Promise<ApiResponse<{ verificationStatus: string; summary: string }>> {
  const res = await api.post<ApiResponse<{ verificationStatus: string; summary: string }>>(
    `/admin/verification/companies/${companyId}/revalidate`,
    {},
  );
  return res.data;
}

export async function cancelRejection(
  companyId: string,
  data: { reason?: string },
): Promise<ApiResponse<{ verificationStatus: string }>> {
  const res = await api.post<ApiResponse<{ verificationStatus: string }>>(
    `/admin/verification/companies/${companyId}/cancel-rejection`,
    data,
  );
  return res.data;
}

/**
 * **검증 서류 내려받기** — 심사자가 지정서·사업자등록증 파일을 보는 유일한 경로(2026-10-19 신설).
 *
 * ## ⚠️ `VerificationDocument.url`을 `<a href>`·`<img src>`에 넣지 말 것 — **401**이다
 * 서버 `jwt.strategy.ts`가 Bearer 헤더 전용(쿠키 미지원)이라 평범한 내비게이션은 인증을 잃는다.
 * 그 `url`은 **형상 호환**으로 남아 있고(응답 계약이 조직 상세와 같은 타입을 쓴다), 실제 열람은
 * 이 함수로 blob을 받아서 한다.
 *
 * ## 왜 날 `fetch` + 토큰이 아니라 공용 axios 인스턴스인가
 * 그 인스턴스의 인터셉터가 **Bearer 주입과 401 refresh 큐**를 소유한다. `pages/UserFeedback.tsx`의
 * 첨부 썸네일은 `fetch(url, { headers: { Authorization } })`로 토큰을 직접 들고 가는데, 그러면
 * **refresh를 우회**해 액세스 토큰이 만료된 뒤 그 썸네일만 조용히 깨진다(나머지 화면은 돈다).
 * 같은 일을 하는 web 형제도 공용 인스턴스를 쓴다(`apps/web/src/lib/organizations.ts`
 * `downloadVerificationDocument`).
 *
 * ## ⚠️ 에러 본문도 Blob이다
 * `responseType: 'blob'`이면 서버 문구를 읽을 수 없다(읽으려면 `blob.text()` + JSON 파싱이 한 겹
 * 더 필요하다) — 호출부는 `extractApiError`류를 쓰지 말고 일반 문구로 떨어뜨린다.
 */
export async function downloadVerificationDocument(
  companyId: string,
  docId: string,
): Promise<Blob> {
  const res = await api.get(
    `/admin/verification/companies/${companyId}/documents/${docId}`,
    { responseType: 'blob' },
  );
  return res.data as Blob;
}
